import React, { useEffect, useState } from "react";
import {
  PieChart,
  Pie,
  Cell,
  Legend,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import moment from "moment";
import { useSelector } from "react-redux";

const donutColors = ["#FF85B6", "#83bcebff"];

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const { value, date } = payload[0].payload;
    return (
      <div
        style={{
          padding: "10px",
          color: "#333",
          backgroundColor: "#fff",
          borderRadius: "8px",
          boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
        }}
      >
        <div className="flex gap-2">
          <strong style={{ margin: 0, color: "#333" }}>Value:</strong>
          <p style={{ color: "#333" }}>{value}</p>
        </div>
        <div className="flex gap-2">
          <strong style={{ margin: 0, color: "#333" }}>Date:</strong>
          <p style={{ color: "#333" }}>{moment(date).format("DD-MM-YYYY")}</p>
        </div>
      </div>
    );
  }
  return null;
};

const CombinedCharts = ({
  graphROI,
  selectedAsset,
  graphLoading,
  companyLoading,
}) => {
  const [pieChartData, setPieChartData] = useState([]);
  const [activeIndex, setActiveIndex] = useState(null);
  const theme = useSelector((state) => state.theme.mode);

  const processWaterfall = (data) => {
    let cumulative = 0;
    let initial = 0;
    let total = 0;

    return data.map((d) => {
      let delta = parseFloat(d.delta || d.value || 0);
      let base = total;
      let color = "positive";

      if (d.is_initial) {
        // Initial value
        cumulative = d.value;
        initial = d.value;
        total = d.value;
        delta = d.value;
        color = "positive";
      } else if (d.type === "INCOME" || d.type === "IN" || d.type === "BUY") {
        cumulative += delta;
        total += Math.abs(d.value) || d.value;
        color = "positive";
      } else if (d.type === "EXPENSE" || d.type === "SELL") {
        delta = -Math.abs(delta);
        cumulative += delta;
        total -= Math.abs(d.value);
        color = "negative";
      } else if (d.type === "VALUATION") {
        delta = d.value - total;
        total += delta;
        cumulative = delta;
        color = delta >= 0 ? "positive" : "negative";
      }

      return {
        label: d.type,
        base,
        delta,
        value: total,
        date: d.date,
        color,
      };
    });
  };

  useEffect(() => {
    if (graphROI) {
      if (graphROI?.pieChartData) {
        setPieChartData([
          {
            name: graphROI?.pieChartData.type,
            value: graphROI?.pieChartData.ownership,
          },
          { name: "Company", value: graphROI?.pieChartData.company },
        ]);
      }
    }
  }, [graphROI]);

  const data = processWaterfall(graphROI?.waterFallChartData?.data || []);

  const formatTick = (value) => {
    const format = (num, suffix) => {
      let str = num.toFixed(1);
      if (str.endsWith(".0")) str = str.slice(0, -2);
      return str + suffix;
    };

    if (value >= 1e12 || value <= -1e12) {
      return format(value / 1e12, "T");
    } else if (value >= 1e9 || value <= -1e9) {
      return format(value / 1e9, "B");
    } else if (value >= 1e6 || value <= -1e6) {
      return format(value / 1e6, "M");
    } else if (value >= 1e3 || value <= -1e3) {
      return format(value / 1e3, "K");
    }
    return value.toString();
  };

  return (
    <div className="md:flex gap-4 lg:gap-16 md:space-y-0 space-y-5 mt-8">
      <div className="p-5 border border-lightPrimary/40 dark:border-info/40 md:w-[50%] dark:bg-custom-radial bg-white">
        {graphLoading || companyLoading ? (
          <div className="animate-pulse space-y-4">
            <div className="h-6 w-40 bg-gray-200 dark:bg-gray-700 rounded mx-auto"></div>
            <div className="h-[30rem] bg-gray-200 dark:bg-gray-700 rounded"></div>
          </div>
        ) : (
          <>
            <h2 className="text-2xl text-lightPrimary dark:text-primary-gradient font-semibold text-center">
              Ownership
            </h2>
            <ResponsiveContainer height={400}>
              <PieChart>
                <Tooltip
                  cursor={{ fill: "none" }}
                  content={({ payload }) => {
                    if (!payload || payload.length === 0) return null;

                    return (
                      <div className="p-3 bg-white rounded-lg shadow-md text-gray-800">
                        <div className="flex gap-2">
                          <strong className="m-0 text-gray-800">Name:</strong>
                          <p className="text-gray-800">
                            {payload[0]?.payload.name}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <strong className="m-0 text-gray-800">Value:</strong>
                          <p className="text-gray-800">
                            {payload[0]?.payload.value}
                          </p>
                        </div>
                      </div>
                    );
                  }}
                />

                <Pie
                  data={pieChartData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={80}
                  paddingAngle={2}
                  dataKey="value"
                  onMouseEnter={(_, index) => setActiveIndex(index)}
                  onMouseLeave={() => setActiveIndex(null)}
                  activeIndex={activeIndex}
                  activeShape={{ outerRadius: 90 }}
                >
                  {pieChartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={donutColors[index]} />
                  ))}
                </Pie>

                <Legend
                  verticalAlign="bottom"
                  align="center"
                  content={() => (
                    <div className="flex justify-center gap-4 mt-2">
                      {pieChartData.map((entry, index) => (
                        <div key={index} className="flex items-center gap-1">
                          <div
                            style={{
                              width: 12,
                              height: 12,
                              backgroundColor: donutColors[index],
                              borderRadius: 2,
                            }}
                          ></div>
                          <span className="text-lightPrimary dark:text-primary-gradient text-sm">
                            {entry.name}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                />
              </PieChart>
            </ResponsiveContainer>
          </>
        )}
      </div>
      <div className="border border-lightPrimary/40 dark:border-info/40 p-5 md:w-[50%] dark:bg-custom-radial bg-white">
        {graphLoading || companyLoading ? (
          <div className="animate-pulse space-y-4">
            <div className="h-6 w-40 bg-gray-200 dark:bg-gray-700 rounded mx-auto"></div>
            <div className="h-[30rem] bg-gray-200 dark:bg-gray-700 rounded"></div>
          </div>
        ) : (
          <>
            <h2 className="text-2xl text-lightPrimary dark:text-primary-gradient font-semibold text-center">
              {selectedAsset?.name ? selectedAsset?.name : "--"}
            </h2>
            {data.length > 0 && (
              <ResponsiveContainer
                width="100%"
                height={500}
                className={
                  data?.length <= 2
                    ? "mx-0 xl:mx-10 mt-8"
                    : `mx-0 xl:mx-10 mt-8`
                }
                style={{
                  maxWidth: data.length <= 2 ? `${data.length * 15}rem` : "80%",
                }}
              >
                <BarChart
                  data={data}
                  margin={{ top: 20, right: 20, left: 10, bottom: 20 }}
                  barCategoryGap={0}
                  barGap={0}
                >
                  <defs>
                    <linearGradient
                      id="positiveGradient"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop
                        offset="5%"
                        stopColor="#d4e3eeff"
                        stopOpacity={0.9}
                      />
                      <stop
                        offset="90%"
                        stopColor="#6dbee4ff"
                        stopOpacity={0.9}
                      />
                      <stop
                        offset="105%"
                        stopColor="#b7dcfaff"
                        stopOpacity={0.8}
                      />
                    </linearGradient>

                    <linearGradient
                      id="negativeGradient"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop offset="5%" stopColor="#e7c898" stopOpacity={0.9} />
                      <stop
                        offset="95%"
                        stopColor="#EA8100"
                        stopOpacity={0.9}
                      />
                      <stop
                        offset="105%"
                        stopColor="#f19019"
                        stopOpacity={0.9}
                      />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="0 3" />
                  <XAxis
                    dataKey="label"
                    tickLine={false}
                    tick={{
                      fill: theme === "dark" ? "#cecece" : "#a5a5a8",
                      fontSize: 12,
                      fontWeight: 500,
                    }}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{
                      fill: theme === "dark" ? "#cecece" : "#a5a5a8",
                      fontSize: 12,
                      fontWeight: 500,
                    }}
                    tickFormatter={formatTick}
                  />
                  <Tooltip content={<CustomTooltip />} cursor={false} />
                  <Bar dataKey="base" stackId="a" fill="transparent" />

                  {/* Delta bar */}
                  <Bar
                    dataKey="delta"
                    stackId="a"
                    radius={[4, 4, 0, 0]}
                    maxBarSize={data.length <= 2 ? 200 : undefined}
                  >
                    {data.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={
                          entry.color === "negative"
                            ? "url(#negativeGradient)"
                            : "url(#positiveGradient)"
                        }
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default CombinedCharts;
